import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';
import { ConfirmComponent, ConfirmStatus } from './confirm/confirm.component';
import { INTERACTION_MODAL_DIALOG_CLASS, INTERACTION_MODAL_OPTIONS, INTERACTION_MODAL_SWAP_OPTIONS } from './interaction-modal.config';

export interface ChooseButton{
    name: string;
    value: string | ConfirmStatus;
}

export interface ConfirmButton {
    label: string;
    value?: ConfirmStatus | string;
    style?: 'default' | 'danger' | 'primary' | 'secondary';
    isPrimary?: boolean;
}

export interface ConfirmOptions {
    title?: string;
    subtitle?: string;
    message: string;
    modalWidth?: string;
    renderAsHtml?: boolean;
    buttons?: ConfirmButton[];
    cancelButtonLabel?: string;
    confirmButtonLabel?: string;
    confirmButtonStyle?: 'default' | 'danger' | 'primary' | 'secondary';
}

export type PickedLibrary = 'EXERCISE' | 'PLAN' | 'WORKOUT' | 'CANCELED';

@Injectable()

export class InteractionService {
    private keepBackdropUntilNextShow = false;

    constructor(private modalService: BsModalService) {
        this.modalService.onShown.subscribe(() => {
            if (!this.keepBackdropUntilNextShow) {
                return;
            }
            this.keepBackdropUntilNextShow = false;
            this.alignModalCountToLoaders();
            this.ensureBackdrop();
            this.ensureBackdrop();
        });
    }

    private get confirmModalOptions() {
        return {
            class: INTERACTION_MODAL_DIALOG_CLASS,
            ...INTERACTION_MODAL_OPTIONS,
        };
    }

    /** Hide a modal, then run callback once it is fully removed from the DOM. */
    public runAfterHidden(modalRef: BsModalRef, callback: () => void): void {
        modalRef.onHidden?.pipe(take(1)).subscribe(() => callback());
        this.hideWithoutAnimation(modalRef);
    }

    /** Swap the current modal for a confirm dialog, then restore the previous modal when closed. */
    public confirmReplacing(
        parentRef: BsModalRef,
        options: ConfirmOptions,
        restoreParent: () => void,
    ): Promise<boolean> {
        return new Promise(resolve => {
            this.runAfterHidden(parentRef, () => {
                const initialState = this.buildConfirmInitialState(options, (status: ConfirmStatus) => {
                    const confirmed = status === ConfirmStatus.CONFIRMED;
                    this.bsModalRef.onHidden?.pipe(take(1)).subscribe(() => {
                        if (!confirmed) {
                            restoreParent();
                            this.keepBackdropUntilNextShow = true;
                            this.ensureBackdrop();
                            setTimeout(() => this.ensureBackdrop());
                        }
                        resolve(confirmed);
                    });
                    this.hideWithoutAnimation(this.bsModalRef);
                });

                this.bsModalRef = this.modalService.show(ConfirmComponent, {
                    initialState,
                    ...this.confirmModalOptions,
                    ...INTERACTION_MODAL_SWAP_OPTIONS,
                });
                this.ensureBackdrop();
            });
        });
    }

    public info(content: string, renderAsHtml?: boolean): Promise<boolean> {
        const promise = new Promise<boolean>((resolve, reject) => {
            const initialState = {
                title: content,
                hideCancel: true,
                renderAsHtml: renderAsHtml,
                confirmButtonStyle: 'primary' as const,
                onClose: (status: ConfirmStatus) => {
                    status === ConfirmStatus.CONFIRMED ? resolve(true) : resolve(false);
                }
            };
            
            this.bsModalRef = this.modalService.show(ConfirmComponent, {
                initialState,
                ...this.confirmModalOptions,
            });
        })
        
        return promise;
    }

    public confirm(question: string | ConfirmOptions, renderAsHtml?: boolean): Promise<boolean> {
        const promise = new Promise<boolean>((resolve, reject) => {
            // Backward compatibility: if question is a string, use old format
            if (typeof question === 'string') {
                const initialState = {
                    title: question,
                    renderAsHtml: renderAsHtml,
                    confirmButtonStyle: 'primary' as const,
                    onClose: (status: ConfirmStatus) => {
                        status === ConfirmStatus.CONFIRMED ? resolve(true) : resolve(false);
                    }
                };
                
                this.bsModalRef = this.modalService.show(ConfirmComponent, {
                    initialState,
                    ...this.confirmModalOptions,
                });
            } else {
                const options = question as ConfirmOptions;
                const initialState = this.buildConfirmInitialState(options, (status: ConfirmStatus) => {
                    status === ConfirmStatus.CONFIRMED ? resolve(true) : resolve(false);
                });

                this.bsModalRef = this.modalService.show(ConfirmComponent, {
                    initialState,
                    ...this.confirmModalOptions,
                });
            }
        })
        
        return promise;
    }

    public confirmButtonChoice(options: ConfirmOptions & { buttons: ConfirmButton[] }): Promise<string | null> {
        return new Promise(resolve => {
            const initialState = this.buildConfirmInitialState(options, (status: ConfirmStatus | string) => {
                if (status === ConfirmStatus.CANCELED || status === 'cancel') {
                    resolve(null);
                    return;
                }

                resolve(String(status));
            });

            this.bsModalRef = this.modalService.show(ConfirmComponent, {
                initialState,
                ...this.confirmModalOptions,
            });
        });
    }

    public choose(question: string, buttonlist: ChooseButton[]): Promise<PickedLibrary> {
        const promise = new Promise<PickedLibrary>((resolve, reject) => {
            const initialState = {
                title: question,
                multiple: true,
                buttonlist,
                onPick: (status: PickedLibrary) => {
                    if (status === 'EXERCISE') {
                        resolve('EXERCISE');
                    } 
                    if (status === 'WORKOUT'){
                        resolve('WORKOUT');
                    }
                    if (status === 'PLAN') {
                        resolve('PLAN');
                    }
                    else {
                        resolve('CANCELED');
                    }
                }
            };
            
            this.bsModalRef = this.modalService.show(ConfirmComponent, {
                initialState,
                ...this.confirmModalOptions,
            });
        })
        
        return promise;
    }

    private buildConfirmInitialState(
        options: ConfirmOptions,
        onClose: (status: ConfirmStatus) => void,
    ) {
        return {
            title: options.title || 'Confirm',
            subtitle: options.subtitle,
            message: options.message,
            modalWidth: options.modalWidth || '440px',
            renderAsHtml: options.renderAsHtml !== undefined ? options.renderAsHtml : false,
            buttons: options.buttons,
            cancelBtnName: options.cancelButtonLabel || 'Cancel',
            confirmBtnName: options.confirmButtonLabel || 'Confirm',
            confirmButtonStyle: options.confirmButtonStyle || 'primary',
            hideCancel: options.buttons ? true : false,
            onClose,
        };
    }

    private getModalInternals(): ModalServiceInternals {
        return this.modalService as unknown as ModalServiceInternals;
    }

    /**
     * Remove a modal immediately without the fade-out, and without tearing down
     * the shared backdrop (BsModalService.hide() would hide it when count === 1).
     */
    private hideWithoutAnimation(modalRef: BsModalRef): void {
        const service = this.getModalInternals();
        const loaders = service.loaders ?? [];
        const index = loaders.findIndex(item => item.instance?.config?.id === modalRef.id);
        const loader = index >= 0 ? loaders[index] : null;
        document.querySelector('modal-container.modal.show')?.classList.remove('fade');

        if (loader?.instance) {
            loader.instance.isAnimated = false;
            loader.instance.isShown = false;
            loader.instance.isModalHiding = true;
        }

        // ConfirmComponent always calls bsModalRef.hide() after onClose.
        // No-op it so that second hide cannot tear down the shared backdrop.
        modalRef.hide = () => undefined;

        if (!loader) {
            return;
        }

        this.keepBackdropUntilNextShow = true;
        loader.hide(modalRef.id);
        const currentIndex = loaders.indexOf(loader);
        if (currentIndex >= 0) {
            loaders.splice(currentIndex, 1);
        }
        const active = loaders.filter(item => item.instance).length;
        service.modalsCount = Math.max(active, 1);
        this.ensureBackdrop();
    }

    private ensureBackdrop(): void {
        const existing = document.querySelector('.modal-backdrop') as HTMLElement | null;
        if (existing) {
            existing.classList.add('show');
            existing.style.backgroundColor = 'var(--cms-overlay-bg)';
            existing.style.animation = 'none';
            return;
        }

        const service = this.getModalInternals();
        const previousCount = service.modalsCount;
        service.modalsCount = 1;
        service.backdropRef = undefined;
        this.modalService._showBackdrop();
        service.modalsCount = Math.max(previousCount, 1);
        const created = document.querySelector('.modal-backdrop') as HTMLElement | null;
        if (created) {
            created.classList.add('show');
            created.style.backgroundColor = 'var(--cms-overlay-bg)';
            created.style.animation = 'none';
        }
    }

    private alignModalCountToLoaders(): void {
        const service = this.getModalInternals();
        const active = (service.loaders ?? []).filter(item => item.instance).length;
        if (active > 0) {
            service.modalsCount = active;
        }
    }

    private bsModalRef: BsModalRef;
}

interface ModalLoader {
    instance?: {
        isAnimated: boolean;
        isShown: boolean;
        isModalHiding: boolean;
        config?: { id?: number | string };
    };
    hide: (id?: number | string) => void;
}

interface ModalServiceInternals {
    loaders: ModalLoader[];
    modalsCount: number;
    backdropRef?: unknown;
}
